import OneToMany from 'components/OneToMany/OneToMany';
import { DatePicker } from '@groupelacentrale/contact-storybook';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { VehicleMatch, VehicleType, VehicleVersionFields, VehicleVersionFieldsList } from 'types';
import { vehicleEnergyMapping } from 'utils/vehicleEnergy';
import { vehicleCategoryMapping } from 'utils/vehicleCategory';
import VehicleVersionList from './VehicleVersionList';

type Fields = VehicleVersionFields & { firstCirculationDate?: Date };

interface Props {
  vehicleType: VehicleType;
  isMatched: boolean;
  values: Fields;
  vehicleMatchList: VehicleMatch[];
  vehicleVersionFieldsList: VehicleVersionFieldsList;
  eligibleVehicleVersionFieldsList: VehicleVersionFieldsList;
  refs?: Record<keyof Fields, React.RefObject<HTMLDivElement>>;
  errors: FormikErrors<Fields>;
  touched: FormikTouched<Fields>;
  handleFirstCirculationDateChange?: (value: Date) => Promise<void>;
  handleMakeChange?: (firstCirculationDate: Date, value: string) => Promise<void>;
  handleModelChange?: (firstCirculationDate: Date, make: string, value: string) => Promise<void>;
  handleFilterableFieldChange: (
    filteredValues: VehicleVersionFields,
    oldValues: VehicleVersionFields,
  ) => VehicleVersionFields | undefined;
  handleVersionChange: (value: string) => VehicleVersionFields;
}

export const VehicleVersionFormView: React.FC<Props & FormikProps<Fields>> = ({
  vehicleType,
  isMatched,
  values,
  vehicleMatchList,
  vehicleVersionFieldsList,
  eligibleVehicleVersionFieldsList,
  refs,
  errors,
  touched,
  handleFirstCirculationDateChange,
  handleMakeChange,
  handleModelChange,
  handleFilterableFieldChange,
  handleVersionChange,
  setFieldValue,
  setValues,
}) => {
  const onFieldValueChange = useCallback(
    (name: string, selectedValue: string) => {
      const newValues = handleFilterableFieldChange(
        {
          [name]: selectedValue,
        },
        values,
      );
      setValues(oldValues => ({ ...oldValues, ...newValues }));
    },
    [setValues, handleFilterableFieldChange, values],
  );

  const onDateChange = useCallback(
    async (selectedValue: Date) => {
      setValues(oldValues => ({
        ...oldValues,
        firstCirculationDate: selectedValue,
        make: undefined,
        model: undefined,
        category: undefined,
        energy: undefined,
        doors: undefined,
        gearbox: undefined,
        motorizationFull: undefined,
        trimLevel: undefined,
        fiscalHorsePower: undefined,
        version: undefined,
      }));
      await handleFirstCirculationDateChange?.(selectedValue);
    },
    [handleFirstCirculationDateChange, setValues],
  );

  const onMakeChange = useCallback(
    async (selectedValue: string) => {
      setValues(oldValues => ({
        ...oldValues,
        make: selectedValue,
        model: undefined,
        category: undefined,
        energy: undefined,
        doors: undefined,
        gearbox: undefined,
        motorizationFull: undefined,
        trimLevel: undefined,
        fiscalHorsePower: undefined,
        version: undefined,
      }));
      await handleMakeChange?.(values.firstCirculationDate!, selectedValue);
    },
    [handleMakeChange, values.firstCirculationDate, setValues],
  );

  const onModelChange = useCallback(
    async (selectedValue: string) => {
      if (isMatched) {
        onFieldValueChange('model', selectedValue);
      } else {
        setValues(oldValues => ({
          ...oldValues,
          model: selectedValue,
          category: undefined,
          energy: undefined,
          doors: undefined,
          gearbox: undefined,
          motorizationFull: undefined,
          trimLevel: undefined,
          fiscalHorsePower: undefined,
          version: undefined,
        }));
        await handleModelChange?.(values.firstCirculationDate!, values.make!, selectedValue);
      }
    },
    [isMatched, handleModelChange, values.firstCirculationDate, values.make, setValues],
  );

  const onVersionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('version', e.currentTarget.value);
    },
    [setFieldValue],
  );

  useEffect(() => {
    if (values.version && vehicleMatchList.length > 0) {
      setValues(oldValues => ({
        ...oldValues,
        ...handleVersionChange(values.version!),
      }));
    }
  }, [values.version, vehicleMatchList, setValues, handleVersionChange]);

  useEffect(() => {
    (async () => {
      if (!isMatched && vehicleVersionFieldsList.model.length === 1 && vehicleVersionFieldsList.version.length === 0) {
        await onModelChange(vehicleVersionFieldsList.model[0]);
      }
    })();
  }, [isMatched, vehicleVersionFieldsList.model, vehicleVersionFieldsList.version, onModelChange]);

  return (
    <div className="vehicle-version-fields">
      <div ref={refs?.firstCirculationDate}>
        <DatePicker
          name="firstCirculationDate"
          label="Date de 1ère mise en circulation*"
          value={values.firstCirculationDate}
          maxDate={new Date()}
          onChange={onDateChange}
          errorMessage={touched.firstCirculationDate ? errors.firstCirculationDate : undefined}
          disabled={isMatched}
        />
      </div>
      <OneToMany
        ref={refs?.make}
        name="make"
        label="Marque*"
        placeholder="Sélectionnez une marque"
        value={values.make}
        options={vehicleVersionFieldsList.make}
        onSelect={onMakeChange}
        errorMessage={touched.make ? errors.make : undefined}
        isDropdownMode
      />
      <OneToMany
        ref={refs?.model}
        name="model"
        label="Modèle*"
        placeholder="Sélectionnez un modèle"
        value={values.model}
        options={vehicleVersionFieldsList.model}
        eligibleOptions={isMatched ? eligibleVehicleVersionFieldsList.model : undefined}
        onSelect={onModelChange}
        errorMessage={touched.model ? errors.model : undefined}
        isDropdownMode
      />
      {((!isMatched && vehicleVersionFieldsList.version.length > 0) || isMatched) && (
        <>
          <OneToMany
            ref={refs?.category}
            name="category"
            label="Catégorie*"
            placeholder="Sélectionnez une catégorie"
            value={values.category}
            options={vehicleVersionFieldsList.category}
            eligibleOptions={eligibleVehicleVersionFieldsList.category}
            translatedOptions={vehicleCategoryMapping}
            onSelect={(selectedValue: string) => onFieldValueChange('category', selectedValue)}
            errorMessage={touched.category ? errors.category : undefined}
            isDropdownMode
          />
          {vehicleType === 'AUTO' && (
            <>
              <OneToMany
                ref={refs?.energy}
                name="energy"
                label="Carburant"
                placeholder="Sélectionnez un carburant"
                value={values.energy}
                options={vehicleVersionFieldsList.energy}
                eligibleOptions={eligibleVehicleVersionFieldsList.energy}
                translatedOptions={vehicleEnergyMapping}
                onSelect={(selectedValue: string) => onFieldValueChange('energy', selectedValue)}
                errorMessage={touched.energy ? errors.energy : undefined}
                isDropdownMode
              />
              <OneToMany
                ref={refs?.doors}
                name="doors"
                label="Nombre de portes"
                placeholder="Sélectionnez un nombre de portes"
                value={values.doors?.toString()}
                options={vehicleVersionFieldsList.doors}
                eligibleOptions={eligibleVehicleVersionFieldsList.doors}
                onSelect={(selectedValue: string) => onFieldValueChange('doors', selectedValue)}
                errorMessage={touched.doors ? errors.doors : undefined}
                isDropdownMode
              />
              <OneToMany
                ref={refs?.gearbox}
                name="gearbox"
                label="Boîte de vitesse"
                placeholder="Sélectionnez une boîte de vitesse"
                value={values.gearbox}
                options={vehicleVersionFieldsList.gearbox}
                eligibleOptions={eligibleVehicleVersionFieldsList.gearbox}
                onSelect={(selectedValue: string) => onFieldValueChange('gearbox', selectedValue)}
                errorMessage={touched.gearbox ? errors.gearbox : undefined}
                isDropdownMode
              />
              {isMatched && vehicleVersionFieldsList.motorizationFull.length > 0 && (
                <OneToMany
                  ref={refs?.motorizationFull}
                  name="motorizationFull"
                  label="Motorisation"
                  placeholder="Sélectionnez une motorisation"
                  value={values.motorizationFull}
                  options={vehicleVersionFieldsList.motorizationFull}
                  eligibleOptions={eligibleVehicleVersionFieldsList.motorizationFull}
                  onSelect={(selectedValue: string) => onFieldValueChange('motorizationFull', selectedValue)}
                  errorMessage={touched.motorizationFull ? errors.motorizationFull : undefined}
                  isDropdownMode
                />
              )}
              {vehicleVersionFieldsList.trimLevel.length > 0 && (
                <OneToMany
                  ref={refs?.trimLevel}
                  name="trimLevel"
                  label="Finition"
                  placeholder="Sélectionnez une finition"
                  value={values.trimLevel}
                  options={vehicleVersionFieldsList.trimLevel}
                  eligibleOptions={eligibleVehicleVersionFieldsList.trimLevel}
                  onSelect={(selectedValue: string) => onFieldValueChange('trimLevel', selectedValue)}
                  errorMessage={touched.trimLevel ? errors.trimLevel : undefined}
                  isDropdownMode
                />
              )}
            </>
          )}
          {vehicleType === 'MOTO' && vehicleVersionFieldsList.cubic.length > 0 && (
            <OneToMany
              ref={refs?.cubic}
              name="cubic"
              label="Cylindrée"
              placeholder="Sélectionnez une cylindrée"
              value={values.cubic?.toString()}
              options={vehicleVersionFieldsList.cubic}
              eligibleOptions={eligibleVehicleVersionFieldsList.cubic}
              onSelect={(selectedValue: string) => onFieldValueChange('cubic', selectedValue)}
              errorMessage={touched.cubic ? errors.cubic : undefined}
              isDropdownMode
            />
          )}
          {vehicleVersionFieldsList.fiscalHorsePower.length > 0 && (
            <OneToMany
              ref={refs?.fiscalHorsePower}
              name="fiscalHorsePower"
              label="Puissance fiscale"
              placeholder="Sélectionnez une puissance fiscale"
              value={values.fiscalHorsePower?.toString()}
              options={vehicleVersionFieldsList.fiscalHorsePower}
              eligibleOptions={eligibleVehicleVersionFieldsList.fiscalHorsePower}
              onSelect={(selectedValue: string) => onFieldValueChange('fiscalHorsePower', selectedValue)}
              errorMessage={touched.fiscalHorsePower ? errors.fiscalHorsePower : undefined}
              isDropdownMode
            />
          )}
          <VehicleVersionList
            ref={refs?.version}
            value={values.version}
            versionList={vehicleVersionFieldsList.version}
            eligibleVersionList={eligibleVehicleVersionFieldsList.version}
            touched={touched.version}
            error={errors.version}
            onVersionChange={onVersionChange}
            setFieldValue={setFieldValue}
          />
        </>
      )}
    </div>
  );
};
